/**
 * The member resignation (eroilmoitus) step of the compose wizard (056).
 *
 * Reached from the Viesti jäsenpalveluun topic step, but NOT a message
 * (D-235): it submits through its own port, creates no case and leaves no
 * inbox or sent entry. Retention notice → one required checkbox → resign /
 * cancel (member-surface §3).
 *
 * A one-way door, so (member-surface §5): no auto-advance, and no optimistic
 * rendering — the outcome is shown only after the register answers. Nothing is
 * remembered after the step: no pending flag, no stored time (P7).
 *
 * Accessibility (member-surface §4): the resign action is `aria-disabled`, not
 * natively disabled, so a keyboard user still meets it and its hint; one
 * persistent live region carries only the in-flight text and is cleared on
 * resolution; the outcome is announced by moving focus to a plain wrapper.
 */

import { type JSX, useEffect, useId, useRef, useState } from 'react';

import { useTranslate } from '../../../i18n/useTranslate.js';
import type {
  ResignationIntent,
  SubmitResignationOutcome,
} from '../../../membership-resignation/MembershipResignationPort.js';
import { useMembershipResignation } from '../../../membership-resignation/useMembershipResignation.js';
import { Button } from '../../../primitives/Button.js';
import { Dialog } from '../../../primitives/Dialog.js';
import { stepColumnClasses, stepNoticeClasses } from './chrome.js';
import { ResignationOutcome } from './ResignationOutcome.js';

const BENEFITS_URL = 'https://www.ytkpalvelut.fi/kaikki-palvelut/';
const LINK_TOKEN = '{link}';

const linkClasses = 'font-bold text-primary underline';
const checkboxRowClasses = 'flex items-start gap-3 text-left text-sm text-foreground';
const checkboxClasses = 'mt-1 h-5 w-5 shrink-0 accent-primary';
const actionsClasses = 'flex flex-col gap-2';
const hintClasses = 'm-0 text-sm text-muted-foreground';
const unavailableClasses = 'cursor-not-allowed opacity-50';

/**
 * Where the step is, for the wizard shell. While `submitting` the shell must not
 * let the member leave (the outcome of a one-way write would be dropped unseen);
 * once `final` there is nothing left to go back to.
 */
export type ResignationPhase = 'editing' | 'submitting' | 'final';

export interface ResignationStepProps {
  /** Leave the step, having submitted nothing. */
  readonly onCancel: () => void;
  /** Close the wizard once a final outcome has been shown. */
  readonly onClose: () => void;
  readonly onPhaseChange?: (phase: ResignationPhase) => void;
  /**
   * Owned by the wizard, not the step, so leaving and re-entering the step
   * after an unresolved outcome re-sends the same key.
   */
  readonly intent: ResignationIntent;
  /** Id for the in-flight live region, so the shell can point at it too. */
  readonly liveRegionId: string;
}

export function ResignationStep({
  onCancel,
  onClose,
  onPhaseChange,
  intent,
  liveRegionId,
}: ResignationStepProps): JSX.Element {
  const t = useTranslate();
  const port = useMembershipResignation();
  const checkboxId = useId();
  const hintId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const outcomeRef = useRef<HTMLDivElement>(null);

  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<SubmitResignationOutcome | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (outcome !== null) outcomeRef.current?.focus();
  }, [outcome]);

  // Derived on every render, never cached: unticking makes it unavailable again.
  const unavailable = !confirmed || submitting;
  // Only an unresolved outcome leaves the door open to try again.
  const final =
    outcome !== null &&
    (outcome.status === 'ok' ||
      (outcome.status === 'refused' && outcome.reason === 'already-pending'));
  const phase: ResignationPhase = submitting ? 'submitting' : final ? 'final' : 'editing';

  useEffect(() => {
    onPhaseChange?.(phase);
  }, [onPhaseChange, phase]);

  const submit = async (): Promise<void> => {
    if (unavailable) return;
    setSubmitting(true);
    let next: SubmitResignationOutcome;
    try {
      next = await port.submitResignation(intent);
    } catch (error: unknown) {
      // The port rejects only on a composition error (no `member.resignation`
      // mounted). The member still gets the truthful "could not confirm"; the
      // cause must not vanish with it.
      // biome-ignore lint/suspicious/noConsole: observability for a composition error with no other error UI in scope
      console.error('resignation step: submit rejected', error);
      next = { status: 'refused', reason: 'unavailable' };
    }
    setSubmitting(false);
    setOutcome(next);
  };

  const [secondBefore, secondAfter] = t('compose.wizard.resignation.retention.second').split(
    LINK_TOKEN,
  );

  return (
    <div className={stepColumnClasses}>
      <Dialog.Title ref={headingRef} tabIndex={-1} size="wizard">
        {t('compose.wizard.resignation.card')}
      </Dialog.Title>

      <div className={`${stepNoticeClasses} flex flex-col gap-3 text-left`}>
        <p className="m-0">{t('compose.wizard.resignation.retention.first')}</p>
        <p className="m-0">
          {secondBefore}
          <a className={linkClasses} href={BENEFITS_URL} target="_blank" rel="noopener noreferrer">
            {t('compose.wizard.resignation.retention.link')}
            <span className="sr-only"> {t('jasenedut.linkNewTabHint')}</span>
          </a>
          {secondAfter}
        </p>
      </div>

      {/* One persistent live region, visible: the in-flight text only, cleared on
          resolution. The button keeps its label, so the text has one channel. */}
      <p id={liveRegionId} aria-live="polite" data-resignation-live="" className={hintClasses}>
        {submitting ? t('compose.wizard.resignation.inFlight') : ''}
      </p>

      {outcome !== null ? (
        <div ref={outcomeRef} tabIndex={-1}>
          <ResignationOutcome outcome={outcome} />
        </div>
      ) : null}

      {final ? (
        <Button variant="outline" onClick={onClose}>
          {t('compose.wizard.close')}
        </Button>
      ) : (
        <>
          <label htmlFor={checkboxId} className={checkboxRowClasses}>
            <input
              id={checkboxId}
              type="checkbox"
              className={checkboxClasses}
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span>{t('compose.wizard.resignation.confirm')}</span>
          </label>

          <div className={actionsClasses}>
            <Button
              aria-disabled={unavailable}
              aria-busy={submitting}
              {...(submitting
                ? { 'aria-describedby': liveRegionId }
                : confirmed
                  ? {}
                  : { 'aria-describedby': hintId })}
              className={unavailable ? unavailableClasses : undefined}
              onClick={() => {
                void submit();
              }}
            >
              {t('compose.wizard.resignation.resign')}
            </Button>
            {confirmed ? null : (
              <p id={hintId} className={hintClasses}>
                {t('compose.wizard.resignation.resignHint')}
              </p>
            )}
            <Button
              variant="outline"
              aria-disabled={submitting}
              {...(submitting ? { 'aria-describedby': liveRegionId } : {})}
              className={submitting ? unavailableClasses : undefined}
              onClick={() => {
                if (!submitting) onCancel();
              }}
            >
              {t('compose.wizard.resignation.cancel')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
